import { useEffect, useEffectEvent, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@legal-500/legal500-design-system";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { researchCms } from "@/api/researchCms.service";
import { usePermissions } from "@/hooks/usePermissions";
import { CmsEmptyState } from "../../../components/ResearchCms";
import { CmsConfirmDialog } from "../../../components/ResearchCms/CmsConfirmDialog/CmsConfirmDialog";
import { UnsavedChangesModal } from "../../../components/ResearchCms/UnsavedChangesModal/UnsavedChangesModal";
import { ROUTES } from "../../../routes";
import { CmsLayout } from "./components/CmsLayout";
import {
  IMPORT_DONE_KEY,
  IMPORT_INVALIDATED_QUERY_KEYS,
  type ImportDoneState,
  broughtCompanyIds,
} from "./components/BpaImport/importDone";
import shared from "./components/BpaImport/importShared.module.css";
import { KIND_LABEL, messageOf } from "./components/BpaImport/importText";
import { ColouredWords } from "./components/BpaImport/ColouredWords";
import {
  PlaceholderBar,
  ReviewGroups,
  ReviewGroupsPlaceholder,
} from "./components/BpaImport/ReviewGroups";
import { useImportData, useImportPreviewQuery } from "./components/BpaImport/useImportData";
import {
  ALL_KINDS,
  type ImportPageState,
  type ImportPreview,
  type SarsScaleMismatch,
  type TierLoss,
  buildImportSelection,
  canMove,
  describeImportSummary,
} from "./utils/bpaImport";
import { importBlockedReason, moveBlockedReason } from "./utils/bpaImportEditionStatus";
import { isImportDirty } from "./utils/bpaImportNavigation";
import {
  dropStaleChoices,
  previewTotals,
  readItemChoiceRefusal,
  resetRefusedChoices,
} from "./utils/bpaImportItemControl";
import {
  emptyImportNote,
  importButtonLabel,
  isEmptyImportIdle,
  removedItemCount,
  replaceWarning,
  reviewEmptyState,
  reviewGroups,
  reviewSummary,
  withLeftOutNote,
} from "./utils/bpaImportReview";
import {
  applicableChoices,
  clearImportChoices,
  loadImportChoices,
  saveImportChoices,
} from "./utils/bpaImportSessionStore";
import { type ResolvedImportUrl, resolveImportUrl } from "./utils/bpaImportUrlState";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import { useSplitImportContext } from "./hooks/useSplitImportContext";
import { splitRemovesText, splitUntickedLine } from "./utils/bpaSplit";
import styles from "./CmsBpaImportReviewPage.module.css";

const PLACEHOLDER_FIGURES = 2;

type Exit = { to?: string; state?: Record<string, ImportDoneState> };

function lockedReason(error: unknown): string | null {
  const { status, detailsRaw, message } = (error ?? {}) as {
    status?: number;
    message?: string;
    detailsRaw?: { reason?: string; status?: string };
  };
  if (status !== 409) return null;
  if (detailsRaw?.reason === "target_edition_locked") {
    return importBlockedReason(detailsRaw.status) ?? message ?? null;
  }
  if (detailsRaw?.reason === "source_edition_locked") {
    return moveBlockedReason(detailsRaw.status) ?? message ?? null;
  }
  return null;
}

function mismatchText(mismatch: SarsScaleMismatch): string {
  const { sourceScale, targetScale } = mismatch;
  const submissions =
    mismatch.scoredSubmissions === 1
      ? "1 submission has"
      : `${mismatch.scoredSubmissions} submissions have`;
  const outcome = "They will be imported without their scores.";
  if (sourceScale === null && targetScale === null) {
    return `${submissions} SARs scores, but neither publication's SARs scale is set. ${outcome}`;
  }
  if (sourceScale === null) {
    return `${submissions} SARs scores, but the source publication's SARs scale isn't set; this publication scores 0–${targetScale}. ${outcome}`;
  }
  if (targetScale === null) {
    return `${submissions} SARs scores on a 0–${sourceScale} scale, but this publication's SARs scale isn't set. ${outcome}`;
  }
  return `${submissions} SARs scores on a 0–${sourceScale} scale; this publication scores 0–${targetScale}. ${outcome}`;
}

function SummarySentence({ sentence }: { sentence: string }) {
  const [before, count, ...after] = sentence.split(/(\d+)/);
  if (count === undefined) return <>{sentence}</>;
  return (
    <>
      {before}
      <strong className={styles.summaryCount}>{count}</strong>
      {after.join("")}
    </>
  );
}

function hiddenClerksText(count: number, targetName: string): string {
  const entries =
    count === 1 ? "1 clerks' commentary entry" : `${count} clerks' commentary entries`;
  return `${entries} will be imported but won't show: ${targetName} has no clerks field.`;
}

function quotedList(names: string[]): string {
  const quoted = names.map(name => `“${name}”`);
  return quoted.length > 1
    ? `${quoted.slice(0, -1).join(", ")} and ${quoted[quoted.length - 1]}`
    : quoted.join("");
}

function tierLossText({ rankings, landingTableNames }: TierLoss): string {
  const lost =
    rankings === 1 ? "1 ranking will lose its tier" : `${rankings} rankings will lose their tier`;
  const lists =
    landingTableNames.length === 1
      ? `the target's list ${quotedList(landingTableNames)} isn't tiered`
      : `the target's lists ${quotedList(landingTableNames)} aren't tiered`;
  return `${lost} because ${lists}.`;
}

function deletedRemovedText(count: number, canDelete: boolean): string {
  const removed =
    count === 1
      ? "1 deleted submission in the target practice area will be removed permanently: a source submission takes its place."
      : `${count} deleted submissions in the target practice area will be removed permanently: source submissions take their place.`;
  return canDelete
    ? removed
    : `${removed} Only an editor with delete permission can run this import.`;
}

function deletedRemovedConfirmText(count: number): string {
  if (count === 1) {
    return "The deleted submission in the target practice area will be removed permanently. It can't be brought back.";
  }
  return `The ${count} deleted submissions in the target practice area will be removed permanently. They can't be brought back.`;
}

function savedFrom(state: ImportPageState) {
  return {
    unticked: state.unticked,
    choices: state.choices,
    itemChoices: state.itemChoices ?? {},
    clicked: state.clicked ?? {},
  };
}

function cleanState(resolved: ResolvedImportUrl): ImportPageState {
  return {
    sourceEditionBpaId: resolved.sourceEditionBpaId as string,
    kinds: resolved.kinds,
    mode: resolved.mode,
    unticked: [],
    choices: {},
    itemChoices: {},
    clicked: {},
  };
}

function startingState(resolved: ResolvedImportUrl, targetId: string): ImportPageState {
  const saved = loadImportChoices(targetId, resolved.sourceEditionBpaId as string);
  return { ...cleanState(resolved), ...saved };
}

function withoutChoices(state: ImportPageState): ImportPageState {
  return { ...state, unticked: [], choices: {}, itemChoices: {}, clicked: {} };
}

function hasItemChoices(state: ImportPageState): boolean {
  return Object.keys(state.itemChoices ?? {}).length > 0;
}

function appliedTo(state: ImportPageState, preview: ImportPreview): ImportPageState {
  const fitted = { ...state, ...applicableChoices(savedFrom(state), preview) };
  return dropStaleChoices(fitted, preview).state;
}

export function CmsBpaImportReviewPage() {
  const { editionId = "", bpaId = "" } = useParams<{ editionId: string; bpaId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const [state, setState] = useState<ImportPageState | null>(null);
  const [exit, setExit] = useState<Exit | null>(null);
  const [fittedFor, setFittedFor] = useState<string | null>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const importedRef = useRef(false);

  const importPath = {
    pathname: ROUTES.research.cmsBpaImport(editionId, bpaId),
    search: searchParams.toString(),
  };

  const data = useImportData({
    editionId,
    bpaId,
    pickedEditionId: searchParams.get("sourceEdition"),
  });
  const {
    targetQuery,
    publicationsQuery,
    sourceInAnotherEdition,
    sourceBpasQuery,
    target,
    targetName,
  } = data;

  const urlReady =
    target !== null &&
    (publicationsQuery.isSuccess || publicationsQuery.isError) &&
    (!sourceInAnotherEdition || !sourceBpasQuery.isPending);
  const urlContext = data.urlContext(canMove(hasPermission));
  const resolved = urlReady && urlContext ? resolveImportUrl(searchParams, urlContext) : null;
  if (state === null && resolved?.sourceEditionBpaId && target && !resolved.blocked) {
    setState(startingState(resolved, target.id));
  }

  const sourceName = data.sourceNameOf(state?.sourceEditionBpaId ?? null);
  const source = data.candidates.find(bpa => bpa.id === state?.sourceEditionBpaId) ?? null;

  const fitted = state === null || !hasItemChoices(state) || fittedFor === state.sourceEditionBpaId;
  const listState = state && (fitted ? state : withoutChoices(state));
  const listSelection =
    listState && listState.kinds.length > 0
      ? buildImportSelection({ ...listState, unticked: [] })
      : null;
  const preview = useImportPreviewQuery(target?.id, listSelection);
  const freshPreview = preview.isSuccess && !preview.isPlaceholderData ? preview.data : null;
  const previewData = fitted ? freshPreview : null;
  const refusal =
    preview.isError && listSelection ? readItemChoiceRefusal(preview.error, listSelection) : null;

  const targetId = target?.id ?? null;
  function commit(next: ImportPageState) {
    setState(next);
    if (targetId && next.sourceEditionBpaId) {
      saveImportChoices(targetId, next.sourceEditionBpaId, savedFrom(next));
    }
  }
  const commitApplied = useEffectEvent((next: ImportPageState) => commit(next));
  const applied = state && freshPreview ? appliedTo(state, freshPreview) : null;
  const appliedDiffers = state && applied && JSON.stringify(applied) !== JSON.stringify(state);
  const markFitted = useEffectEvent((sourceId: string | null) => setFittedFor(sourceId));
  useEffect(() => {
    if (appliedDiffers && applied) commitApplied(applied);
    if (!fitted && freshPreview) markFitted(applied?.sourceEditionBpaId ?? null);
  }, [appliedDiffers, applied, fitted, freshPreview]);
  const viewState = applied ?? state;

  const summarySelection =
    fitted && viewState && viewState.kinds.length > 0 && viewState.unticked.length > 0
      ? buildImportSelection(viewState)
      : null;
  const summaryQuery = useImportPreviewQuery(target?.id, summarySelection);
  const summaryLoaded = summaryQuery.isSuccess && !summaryQuery.isPlaceholderData;
  const summaryPreview = summarySelection
    ? summaryLoaded
      ? summaryQuery.data
      : null
    : previewData;
  const queryError = preview.isError
    ? preview.error
    : summaryQuery.isError
      ? summaryQuery.error
      : null;

  const importMutation = useMutation({
    mutationFn: (dropMismatchedSars: boolean) =>
      researchCms.importContent(target!.id, {
        ...buildImportSelection(viewState!)!,
        mode: viewState!.mode,
        ...(resolved?.purpose ? { purpose: resolved.purpose } : {}),
        ...(dropMismatchedSars ? { dropMismatchedSars: true } : {}),
      }),
    onSuccess: result => {
      importedRef.current = true;
      for (const key of IMPORT_INVALIDATED_QUERY_KEYS) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
      const done: ImportDoneState = {
        sourceEditionBpaId: viewState!.sourceEditionBpaId!,
        sourceBpaName: sourceName,
        organisations: previewTotals(previewData!, viewState!).organisations,
        imported: result.imported,
        withScores: result.scoredSubmissions > 0,
        importedCompanyIds: broughtCompanyIds(previewData!.orgs, viewState!),
      };
      discardSavedChoices();
      if (resolved?.purpose && resolved.hub) setExit({ to: resolved.hub });
      else setExit({ state: { [IMPORT_DONE_KEY]: done } });
    },
  });

  function discardSavedChoices() {
    if (targetId && state?.sourceEditionBpaId)
      clearImportChoices(targetId, state.sourceEditionBpaId);
  }

  const splitContext = useSplitImportContext({
    purpose: resolved?.purpose ?? null,
    hub: resolved?.hub ?? null,
    originalEditionBpaId: state?.sourceEditionBpaId ?? null,
    targetEditionBpaId: target?.id ?? null,
  });
  const changedOnImportPage = location.state?.changed === true;
  const dirty =
    exit === null &&
    state !== null &&
    resolved !== null &&
    (changedOnImportPage || isImportDirty(cleanState(resolved), state));
  const navGuard = useNavigationGuard(dirty, {
    interceptBack: false,
    allowPath: importPath.pathname,
  });

  useEffect(() => {
    if (exit) {
      navigate(exit.to ?? ROUTES.research.cmsBpa(editionId, bpaId), {
        state: exit.state,
        replace: true,
      });
    }
  }, [exit, navigate, editionId, bpaId]);

  useEffect(
    () => () => {
      if (importedRef.current) queryClient.removeQueries({ queryKey: ["bpa-import-preview"] });
    },
    [queryClient],
  );

  if (targetQuery.isLoading) {
    return (
      <CmsLayout>
        <CmsEmptyState loading message="Loading" />
      </CmsLayout>
    );
  }

  if (targetQuery.isError || !target) {
    return (
      <CmsLayout breadcrumbs={[{ label: "Import content" }, { label: "Review" }]}>
        <div className={styles.error} role="alert">
          {targetQuery.isError
            ? messageOf(targetQuery.error)
            : "This practice area is not available to import into."}
        </div>
      </CmsLayout>
    );
  }

  if (!searchParams.get("source") || (resolved && !resolved.sourceEditionBpaId)) {
    return <Navigate to={importPath} replace />;
  }

  const breadcrumbs = [
    {
      label: targetQuery.data?.editionName ?? "",
      path: ROUTES.research.cmsPublication(editionId),
    },
    { label: targetName, path: ROUTES.research.cmsBpa(editionId, bpaId) },
    {
      label: "Import content",
      path: importPath.search ? `${importPath.pathname}?${importPath.search}` : importPath.pathname,
    },
    { label: "Review" },
  ];
  const targetLockedReason = importBlockedReason(targetQuery.data?.editionStatus);
  const blockedMessage = targetLockedReason ?? resolved?.blocked?.reason ?? null;

  if (blockedMessage) {
    const backTo = targetLockedReason
      ? { label: "Back to practice area", path: ROUTES.research.cmsBpa(editionId, bpaId) }
      : { label: "Back to the hub", path: resolved?.blocked?.hub ?? "" };
    return (
      <CmsLayout breadcrumbs={breadcrumbs}>
        <div className={styles.column}>
          <div className={styles.heading}>
            <h1 className={styles.title}>Review import</h1>
          </div>
          <div className={shared.callout} role="status">
            {blockedMessage}
          </div>
          <div>
            <Button variant="outlined" color="default" onClick={() => navigate(backTo.path)}>
              {backTo.label}
            </Button>
          </div>
        </div>
      </CmsLayout>
    );
  }

  const mode = viewState?.mode ?? "copy";
  const mismatch = summaryPreview?.summary.sarsScaleMismatch ?? null;
  const deletedRemoved = summaryPreview?.summary.deletedSubmissionsRemoved ?? 0;
  const canDelete = canMove(hasPermission);
  const previewLocked = queryError ? lockedReason(queryError) : null;
  const emptyState =
    viewState && (viewState.kinds.length === 0 || previewData)
      ? reviewEmptyState(viewState.kinds, previewData, viewState)
      : null;
  const emptyImportIdle =
    viewState !== null &&
    previewData !== null &&
    isEmptyImportIdle({
      nothingToCopy: emptyState === "nothing_to_copy",
      removing: removedItemCount(previewData, viewState),
      purpose: resolved?.purpose ?? null,
    });
  const summaryMissing = summarySelection !== null && summaryPreview === null;
  const summaryPending = summaryMissing && !queryError;
  const ready = viewState !== null && previewData !== null && !refusal && !summaryMissing;
  const loadingFirst =
    viewState === null ||
    (!fitted && !preview.isError) ||
    preview.isLoading ||
    preview.isPlaceholderData ||
    summaryPending;
  const previewFailure = queryError && !refusal && !previewLocked ? messageOf(queryError) : null;
  const importLocked = importMutation.isError ? lockedReason(importMutation.error) : null;
  const importRefusal =
    importMutation.isError && viewState
      ? readItemChoiceRefusal(importMutation.error, buildImportSelection(viewState)!)
      : null;
  const importFailure =
    importMutation.isError && !importLocked && !importRefusal
      ? messageOf(importMutation.error)
      : null;
  const activeRefusal = refusal ?? importRefusal;
  const warning = ready && viewState ? replaceWarning(previewData, viewState) : null;
  const summaryLine =
    ready && viewState
      ? describeImportSummary(mode, previewTotals(previewData, viewState), targetName)
      : null;
  const canImport = ready && emptyState !== "no_kinds";

  const splitRemoves =
    resolved?.purpose === "split" && source && ready && viewState
      ? splitRemovesText(
          Object.values(previewTotals(previewData, viewState).counts).reduce(
            (total, count) => total + count,
            0,
          ),
        )
      : null;
  const untickedLine =
    splitContext && viewState && viewState.unticked.length > 0
      ? splitUntickedLine(splitContext.otherCardMoved, splitContext.otherName)
      : null;

  function backToImport() {
    if (location.state?.fromImport) navigate(-1);
    else navigate(importPath, { replace: true });
  }

  function resetRefused() {
    if (viewState && activeRefusal) {
      importMutation.reset();
      commit(resetRefusedChoices(viewState, activeRefusal));
    }
  }

  const leavePromptOpen = navGuard.blocked;
  const whereLine = [targetQuery.data?.editionName, target.rankingEntityName]
    .filter(Boolean)
    .join(" · ");

  return (
    <CmsLayout breadcrumbs={breadcrumbs}>
      <div className={styles.page}>
        <div className={styles.column}>
          <div className={styles.header}>
            <div className={styles.heading}>
              <h1 className={styles.title}>Review import</h1>
              {whereLine && <p className={styles.where}>{whereLine}</p>}
            </div>

            {viewState && (
              <div className={styles.facts}>
                <span className={styles.mode}>{mode === "move" ? "Move" : "Copy"}</span>
                <span className={styles.kinds}>
                  {ALL_KINDS.filter(kind => viewState.kinds.includes(kind))
                    .map(kind => KIND_LABEL[kind])
                    .join(" · ")}
                </span>
              </div>
            )}
          </div>

          {viewState && (
            <div className={styles.sides} role="group" aria-label="Sides">
              <span className={styles.sideWord} data-side="incoming" data-row="labels">
                From
              </span>{" "}
              <span className={styles.named} data-side="incoming" data-row="names">
                {sourceName}
              </span>{" "}
              <span className={styles.sideWord} data-side="existing" data-row="labels">
                Into
              </span>{" "}
              <span className={styles.named} data-side="existing" data-row="names">
                {targetName}
              </span>
            </div>
          )}

          {mismatch && (
            <div className={shared.callout} role="status">
              {mismatchText(mismatch)}
            </div>
          )}

          {summaryPreview && summaryPreview.summary.hiddenClerksCommentary ? (
            <div className={shared.callout} role="status">
              {hiddenClerksText(summaryPreview.summary.hiddenClerksCommentary, targetName)}
            </div>
          ) : null}

          {summaryPreview?.summary.tierLoss && summaryPreview.summary.tierLoss.rankings > 0 ? (
            <div className={shared.callout} role="status">
              {tierLossText(summaryPreview.summary.tierLoss)}
            </div>
          ) : null}

          {deletedRemoved > 0 && (
            <div className={shared.callout} role="status">
              {deletedRemovedText(deletedRemoved, canDelete)}
            </div>
          )}

          {emptyState === "no_kinds" && (
            <div className={shared.callout} role="status">
              Choose at least one kind of content to import.
            </div>
          )}

          {emptyState === "nothing_to_copy" && (
            <div className={shared.callout} role="status">
              {mode === "move" ? "Nothing will be moved." : "Nothing will be copied."}
            </div>
          )}

          {previewLocked && (
            <div className={shared.errorBox} role="alert">
              <span>{previewLocked}</span>
            </div>
          )}

          {previewFailure && (
            <div className={shared.errorBox} role="alert">
              <span>{previewFailure}</span>
              <Button
                variant="outlined"
                color="default"
                size="small"
                onClick={() => (preview.isError ? preview : summaryQuery).refetch()}
              >
                Retry
              </Button>
            </div>
          )}

          {activeRefusal && (
            <div className={shared.errorBox} role="alert">
              <span>{activeRefusal.reason}</span>
              <Button variant="outlined" color="default" size="small" onClick={resetRefused}>
                Reset changes
              </Button>
            </div>
          )}

          {loadingFirst && (
            <div className={styles.placeholder} role="status" aria-label="Loading review">
              <div className={styles.figures} data-placeholder="figures">
                {Array.from({ length: PLACEHOLDER_FIGURES }, (_, index) => (
                  <div key={index} className={styles.figure}>
                    <PlaceholderBar className={styles.figureValuePlaceholder} />
                    <PlaceholderBar className={styles.figureLabelPlaceholder} />
                  </div>
                ))}
              </div>
              <ReviewGroupsPlaceholder />
            </div>
          )}

          {!loadingFirst && ready && viewState && emptyState !== "no_kinds" && (
            <>
              <dl className={styles.figures}>
                {reviewSummary(previewData, viewState, mode).map(figure => (
                  <div
                    key={figure.label}
                    className={styles.figure}
                    data-warn={figure.warn ? "true" : undefined}
                  >
                    <dt className={styles.figureValue}>{figure.value}</dt>{" "}
                    <dd className={styles.figureLabel}>
                      <ColouredWords text={figure.label} />
                    </dd>
                  </div>
                ))}
              </dl>
              <ReviewGroups
                groups={withLeftOutNote(reviewGroups(previewData, viewState), untickedLine)}
                orgs={previewData.orgs}
                state={viewState}
              />
            </>
          )}
        </div>

        <div className={styles.bar}>
          {importLocked && (
            <div className={shared.errorBox} role="alert">
              <span>{importLocked}</span>
            </div>
          )}

          {importFailure && (
            <div className={shared.errorBox} role="alert">
              <span>{importFailure}</span>
            </div>
          )}

          <div className={styles.barRow}>
            <Button
              variant="outlined"
              color="default"
              className={shared.compactButton}
              onClick={backToImport}
              disabled={importMutation.isPending}
            >
              ‹ Back to import
            </Button>
            <div className={styles.summary}>
              {summaryLine && (
                <span aria-live="polite">
                  <SummarySentence sentence={summaryLine.sentence} />
                </span>
              )}
              {warning && <span className={styles.warning}>{warning}</span>}
              {emptyImportIdle && <span>{emptyImportNote(mode)}</span>}
              {splitRemoves && <span className={styles.warning}>{splitRemoves}</span>}
            </div>
            {canImport && viewState && (
              <Button
                variant="contained"
                color="primary"
                className={shared.compactButton}
                disabled={
                  importMutation.isPending || (deletedRemoved > 0 && !canDelete) || emptyImportIdle
                }
                onClick={() =>
                  deletedRemoved > 0
                    ? setConfirmingRemoval(true)
                    : importMutation.mutate(mismatch !== null)
                }
              >
                {importMutation.isPending
                  ? "Importing..."
                  : importButtonLabel({
                      mode,
                      sarsMismatch: mismatch !== null,
                      nothingToCopy: emptyState === "nothing_to_copy",
                      removing: removedItemCount(previewData, viewState),
                      purpose: resolved?.purpose ?? null,
                    })}
              </Button>
            )}
          </div>
        </div>
      </div>

      {confirmingRemoval && (
        <CmsConfirmDialog
          title="Remove deleted submissions permanently?"
          message={deletedRemovedConfirmText(deletedRemoved)}
          tone="danger"
          confirmLabel="Remove and import"
          cancelLabel="Cancel"
          onCancel={() => setConfirmingRemoval(false)}
          onConfirm={() => {
            setConfirmingRemoval(false);
            importMutation.mutate(mismatch !== null);
          }}
        />
      )}

      {leavePromptOpen && (
        <UnsavedChangesModal
          onCancel={navGuard.cancel}
          onDiscard={() => {
            discardSavedChoices();
            navGuard.proceed();
          }}
        />
      )}
    </CmsLayout>
  );
}
